import { useCallback, useMemo } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  Box, Button, Chip, MenuItem, Stack, ThemeProvider, Typography, createTheme, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import WidthNormalIcon from '@mui/icons-material/WidthNormal';
import { fleetLabelByRowKey } from './fleetCatalog';

const RESULT_COLOR = { PASS: 'success', WARN: 'warning', WARNING: 'warning', FAIL: 'error' };

// Worst-first, so the outcomes you act on head the filter list. Anything a
// producer emits that isn't listed sorts after these, alphabetically.
const RESULT_ORDER = [
  'FAIL', 'WARN', 'WARNING', 'PASS', 'NODATA',
  'NOT RECORDED', 'RECORDED', 'NOOP', 'NOTAPPLICABLE', 'NOTMANAGED',
];

// id -> header, plus per-column hints:
//   `pad`/`max` size the column -- `pad` covers whatever the cell renders beyond
//     plain text (chips, icons), `max` stops one long message eating the viewport.
//   `value` overrides the displayed/measured/exported text when it differs from
//     the raw row field.
//   `filter` picks a dropdown filter instead of a text box. Options are derived
//     from the rows in hand, never hardcoded: the result vocabulary has grown
//     (NOOP, NOTAPPLICABLE, NOTMANAGED, RECORDED) and a fixed list would go stale
//     silently, plus a derived list can't offer an option matching zero rows.
const COLUMN_DEFS = [
  { id: 'account',    header: 'Account',                        filter: 'multi-select' },
  { id: 'target',     header: 'Target',      max: 420 },
  { id: 'checkKey',   header: 'Check',       max: 420,          filter: 'multi-select',
    value: (r) => fleetLabelByRowKey[r.checkKey] || r.checkKey },
  { id: 'result',     header: 'Result',      pad: 110,          filter: 'multi-select',
    order: RESULT_ORDER },
  { id: 'value',      header: 'Value' },
  { id: 'message',    header: 'Message',     max: 520 },
  { id: 'auditDate',  header: 'Date',                           filter: 'multi-select' },
  { id: 'auditRunId', header: 'Producer',                       filter: 'multi-select' },
  { id: 'auditType',  header: 'Audit type',                     filter: 'multi-select' },
  { id: 'targetType', header: 'Target type',                    filter: 'multi-select' },
  { id: 'observedAt', header: 'Observed' },
  { id: 'overridden', header: 'Overridden',                     filter: 'select',
    value: (r) => (r.overridden ? 'Yes' : 'No') },
];

const COLUMN_IDS = new Set(COLUMN_DEFS.map((c) => c.id));

// The text a column shows for a row -- what gets measured, filtered and exported,
// so all four agree.
const valueOf = (def, row) => (def.value ? def.value(row) : row[def.id]);

// Rows sampled when measuring. Measuring 40k rows across 12 columns is half a
// second of canvas work for a width that the first couple of thousand rows
// already settle.
const FIT_SAMPLE = 2000;
// Space a header needs beyond its own text: cell padding, the sort arrow, and the
// column-actions button. Without this the floor is the bare text width and MRT
// truncates the title to "Me…" on any column whose cells are empty.
const HEADER_CHROME = 72;


const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;

function measure(text, font) {
  if (!canvas) return 0;
  const ctx = canvas.getContext('2d');
  ctx.font = font;
  return ctx.measureText(text).width;
}

function cellText(v) {
  if (v === null || v === undefined) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/**
 * Column width: wide enough for the widest sampled cell, but never narrower than
 * the header needs to render its title alongside its icons. An empty column
 * therefore sizes to its own title rather than collapsing.
 */
function fitWidth(def, rows, bodyFont, headFont) {
  const headerMin = Math.ceil(measure(def.header, headFont)) + HEADER_CHROME;

  let widest = 0;
  const sample = rows.length > FIT_SAMPLE ? rows.slice(0, FIT_SAMPLE) : rows;
  for (const r of sample) {
    const m = measure(cellText(valueOf(def, r)), bodyFont);
    if (m > widest) widest = m;
  }
  // `pad` is body-cell padding plus whatever the cell renders around its text
  // (chips on Result), not header chrome.
  const contentWidth = Math.ceil(widest) + (def.pad ?? 24);

  return Math.min(def.max ?? 560, Math.max(headerMin, contentWidth));
}


const BASE_COLUMNS = COLUMN_DEFS.map((c) => c.id);

function extraKeys(rows) {
  const keys = new Set();
  rows.forEach((r) => Object.keys(r.extra || {}).forEach((k) => keys.add(k)));
  return [...keys].sort();
}

function csvCell(v) {
  const s = cellText(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(rows, columnIds, filename) {
  const extras = extraKeys(rows);
  // Export what the column shows, not the raw field: the Check column exports its
  // human label and Overridden exports Yes/No.
  const defs = columnIds.map((id) => COLUMN_DEFS.find((d) => d.id === id));
  const lines = [[...columnIds, ...extras].join(',')];
  rows.forEach((r) => {
    lines.push([
      ...defs.map((d) => csvCell(valueOf(d, r))),
      ...extras.map((k) => csvCell((r.extra || {})[k])),
    ].join(','));
  });
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ResultsTable({ rows, filename }) {
  const theme = useTheme();

  const { bodyFont, headFont } = useMemo(() => {
    const size = theme.typography.fontSize || 14;
    const family = theme.typography.fontFamily;
    return { bodyFont: `${size}px ${family}`, headFont: `600 ${size}px ${family}` };
  }, [theme.typography.fontSize, theme.typography.fontFamily]);

  // Fitted width per column. Doubles as each column's declared `size`, so MRT's
  // own double-click-the-resize-border (which calls column.resetSize()) snaps to
  // the content width rather than back to a fixed default.
  const fitted = useMemo(() => {
    const out = {};
    COLUMN_DEFS.forEach((d) => {
      out[d.id] = fitWidth(d, rows, bodyFont, headFont);
    });
    return out;
  }, [rows, bodyFont, headFont]);

    // Fits against the rows currently in view -- column filters and search applied,
  // across all pages rather than just the visible one. fitWidth seeds from the
  // header width, so a column never ends up narrower than its own title even when
  // every cell in it is empty.
  const applyFit = useCallback(
    (table, ids) => {
      const visible = table.getPrePaginationRowModel().rows.map((r) => r.original);
      const next = {};
      ids.filter((id) => COLUMN_IDS.has(id)).forEach((id) => {
        next[id] = fitWidth(
          COLUMN_DEFS.find((d) => d.id === id),
          visible,
          bodyFont,
          headFont,
        );
      });
      table.setColumnSizing((prev) => ({ ...prev, ...next }));
    },
    [bodyFont, headFont],
  );


  // Dropdown options per filterable column, taken from the rows actually returned.
  // A `order` hint puts the values you act on first; the rest sort alphabetically.
  const filterOptions = useMemo(() => {
    const out = {};
    for (const d of COLUMN_DEFS) {
      if (!d.filter) continue;
      const seen = new Set();
      for (const r of rows) {
        const v = valueOf(d, r);
        if (v === null || v === undefined || v === '') continue;
        seen.add(String(v));
      }
      const rank = (v) => {
        const i = d.order ? d.order.indexOf(String(v).toUpperCase()) : -1;
        return i === -1 ? d.order?.length ?? 0 : i;
      };
      out[d.id] = [...seen].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
    }
    return out;
  }, [rows]);

  const columns = useMemo(
    () =>
      COLUMN_DEFS.map((d) => {
        const col = { accessorKey: d.id, header: d.header, size: fitted[d.id] };

        // A derived display value has to go through accessorFn, or sorting,
        // filtering and the global search would still work off the raw field.
        if (d.value) {
          delete col.accessorKey;
          col.id = d.id;
          col.accessorFn = (row) => valueOf(d, row);
        }

        if (d.filter) {
          col.filterVariant = d.filter;
          col.filterSelectOptions = filterOptions[d.id] || [];
        }

                if (d.id === 'account') {
          // Pinned as the row's identity: dragging or hiding it would leave rows
          // you can't tie back to a customer.
          col.enableColumnOrdering = false;
          col.enableHiding = false;
        }

        if (d.id === 'result') {
          col.Cell = ({ cell, row }) => {
            const v = String(cell.getValue() || '').toUpperCase();
            return (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip size="small" label={cell.getValue() || '—'} color={RESULT_COLOR[v] || 'default'} />
                {row.original.overridden && <Chip size="small" variant="outlined" label="override" />}
              </Stack>
            );
          };
        }
        return col;
      }),
    [fitted, filterOptions],
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableSorting: true,
    enableColumnFilters: true,
    // Filters open from the funnel in each header rather than a subheader row of
    // inputs: a multi-select can't fit inside a column fitted to its content, and
    // it would fight fitWidth's measurements.
    columnFilterDisplayMode: 'popover',
    // MRT builds the filter field as a fullWidth `variant: 'standard'` TextField
    // inside a popover that has no width of its own, so a select collapses to a
    // bare underline that reads as an empty text box. Outlined + labelled + a real
    // minWidth makes it look like the dropdown it already is.
    muiFilterTextFieldProps: ({ column }) => ({
      variant: 'outlined',
      size: 'small',
      label: `Filter ${column.columnDef.header}`,
      sx: { minWidth: 280 },
    }),
    enableGlobalFilter: true,
    enablePagination: true,
    enableRowNumbers: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    enableHiding: true,
    enableColumnOrdering: true,
    // Reordering lives in the Show/Hide Columns panel only. The per-header grab
    // handle clutters a table this wide, and the panel's drag list does the same
    // job with the column names visible.
    enableColumnDragging: false,

    enableColumnPinning: true,
        // Right when closed, down when open -- the usual tree-expand convention.
    // MRT's own icon hardcodes 0deg closed / -180deg open, so the rotation has to
    // come from our own child. Keeping the transition preserves the animation.
    muiExpandButtonProps: ({ row }) => ({
      children: (
        <ExpandMoreIcon
          fontSize="small"
          style={{
            transform: row.getIsExpanded() ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 150ms',
          }}
        />
      ),
    }),
        // Expand-all matches the row chevrons: right when collapsed, down when open.
    // Stays a double chevron so it still reads as "all rows" rather than one.
    muiExpandAllButtonProps: ({ table: t }) => ({
      children: (
        <KeyboardDoubleArrowDownIcon
          fontSize="small"
          style={{
            transform: t.getIsAllRowsExpanded() ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 150ms',
          }}
        />
      ),
    }),

    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      sorting: [{ id: 'account', desc: false }],
      density: 'compact',
            // Row numbers, the expand toggle and Account stay stuck to the left edge --
      // they're how you attribute a row, so they shouldn't scroll away or get
      // dragged into the middle. Everything else is freely reorderable.
      columnPinning: { left: ['mrt-row-numbers', 'mrt-row-expand', 'account'] },
      // Available from the columns button, off by default so the common case
      // isn't buried in metadata.
      columnVisibility: {
        auditRunId: false,
        auditType: false,
        targetType: false,
        observedAt: false,
        overridden: false,
      },
    },
    muiTableContainerProps: { sx: { maxHeight: '58vh' } },
        // MRT's resize handle has its own double-click handler that calls
    // column.resetSize(), snapping back to the static size measured from the whole
    // report. It doesn't stop propagation, so this fires afterwards and wins,
    // re-fitting against whatever is currently filtered into view instead.
    muiTableHeadCellProps: ({ column, table: t }) => ({
      onDoubleClick: (event) => {
        if (!event.target.closest?.('.Mui-TableHeadCell-ResizeHandle-Wrapper')) return;
        if (!COLUMN_IDS.has(column.id)) return;
        event.stopPropagation();
        applyFit(t, [column.id]);
      },
    }),

        renderColumnActionsMenuItems: ({ column, closeMenu, internalColumnMenuItems, table: t }) => [
      // Left pinning only. Matched against the localization string rather than a
      // hardcoded label or menu index, so it survives an MRT version bump that
      // reorders the internal items.
      ...internalColumnMenuItems.filter(
        (item) => item?.props?.label !== t.options.localization?.pinToRight,
      ),
      <MenuItem
        key="fit"
        onClick={() => {
          applyFit(t, [column.id]);
          closeMenu();
        }}
      >
        Fit to content
      </MenuItem>,
    ],
    renderDetailPanel: ({ row }) => {
      const extra = row.original.extra || {};
      const keys = Object.keys(extra);
      if (!keys.length) return null;
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, px: 2, py: 1 }}>
          {keys.map((k) => (
            <Box key={k}>
              <Typography variant="caption" color="text.secondary" display="block">
                {k}
              </Typography>
              <Typography variant="body2">{cellText(extra[k])}</Typography>
            </Box>
          ))}
        </Box>
      );
    },
    // Exports the columns you can see, after sorting and filtering -- so a table
    // narrowed to FAILs with three columns exports exactly that.
    renderTopToolbarCustomActions: ({ table: t }) => (
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ExpandMoreIcon />}
          disabled={!rows.length}
          onClick={() =>
            downloadCsv(
              t.getPrePaginationRowModel().rows.map((r) => r.original),
              t.getVisibleLeafColumns().map((c) => c.id).filter((id) => COLUMN_IDS.has(id)),
              filename,
            )
          }
        >
          Export CSV
        </Button>
        <Button
          size="small"
          variant="text"
          startIcon={<WidthNormalIcon />}
          disabled={!rows.length}
          onClick={() => applyFit(t, t.getVisibleLeafColumns().map((c) => c.id))}
        >
          Fit columns
        </Button>
      </Stack>
    ),
    renderEmptyRowsFallback: () => (
      <Box p={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          No rows for the selected checks, accounts and dates.
        </Typography>
      </Box>
    ),
  });

    // MRT's popup menus aren't customisable through props -- the Show/Hide Columns
  // panel builds its own pin buttons and the button that opens it forwards nothing
  // to the menu. A nested theme reaches them anyway (portals keep React context)
  // while staying scoped to this table.
  const menuTheme = useMemo(
    () =>
      createTheme(theme, {
        components: {
          MuiMenu: {
            styleOverrides: {
              paper: {
                '& .MuiMenuItem-root': {
                  fontSize: '0.8125rem',
                  minHeight: 30,
                  paddingTop: 2,
                  paddingBottom: 2,
                },
                '& .MuiButton-root': { fontSize: '0.72rem', padding: '2px 8px', minWidth: 'auto' },
                '& .MuiIconButton-root': { padding: 3 },
                '& .MuiSvgIcon-root': { fontSize: '1.05rem' },
                '& .MuiSwitch-root': { transform: 'scale(0.8)' },
                // Left pinning only. The right-pin button is the one whose icon
                // carries an inline -90deg rotation, which is the only stable way
                // to single it out without forking the component.
                '& .MuiIconButton-root:has(svg[style*="rotate(-90deg)"])': { display: 'none' },
              },
            },
          },
        },
      }),
    [theme],
  );

  return (
    <ThemeProvider theme={menuTheme}>
      <MaterialReactTable table={table} />
    </ThemeProvider>
  );

}
